import { render } from 'mustache';
import { CONNECTED_CALLBACK, HOST_ELEMENT_KEY, registerNewDirective, setupPrototype, verifyAaSelector } from './renderer';

export interface DomComponentConfig {
  selector: string;
  template?: string;
}

/**
 * Components are reusable instances assigned to a DOM element. Components
 * can be used to render custom Elements to the DOM.
 *
 * Take a look at the following example of a Component which will go through
 * each letter in the alphabet:
 *
 * ```typescript
 * // Define the Alphabet class as a Component
 * @DomComponent({
 *  selector: 'aa-alphabet',
 *  template: `<div class="letters"><div>`
 * })
 * class Alphabet implements OnConnect {
 *  // The {@link @Child} decorator searches in the component for a matching element.
 *  @Child('.letters')
 *  private readonly letterWrapper: HTMLElement;
 *
 *  private readonly letters: String[] = ['a', 'b', 'c'];
 *
 *  // Go through each letter and replace the content of '.letters' with a letter
 *  // of the alphabet.
 *  connected() {
 *    const index = 0;
 *    setInterval(() => {
 *      this.letterWrapper.innerText = this.letters[index];
 *      index = (index == letters.length - 1) ? 0 : index + 1;
 *    }, 2500);
 *  }
 * }
 * ```
 *
 * Using the {@link @DomComponent} decorator we're assigning a new instance of the
 * Alphabet class to each occurence of `<aa-alphabet>` in the DOM. During this 'connection' phase
 * the Elements `innerHTML` will be replaced by the provided template. The template string has Mustache
 * support. This allows for easy binding of instance properties to the component's HTML.
 *
 * Note that there's only support for template property binding during the 'connection' phase.
 * There's no such thing as one- or two-way binding.
 *
 * Implementing the {@link OnConnect} interface and defining the {@link connected()} method allows
 * us to execute additional code after the Component instance has been created.
 *
 * @param config
 */
export const DomComponent = (config: DomComponentConfig) => (cls: any) => {
  verifyAaSelector(config.selector);
  cls = setupPrototype(cls);

  // Prepand connectedCallback with template rendering
  const connectedCallback = cls.prototype[CONNECTED_CALLBACK] || function () { };
  cls.prototype[CONNECTED_CALLBACK] = function () {
    // Render component
    if (config.template) {
      const hostElm = this[HOST_ELEMENT_KEY]();
      hostElm.innerHTML = render((config.template as string), this);
    }

    // Call original connectedCallback
    connectedCallback.call(this);
  };

  // Register new elements
  registerNewDirective(config.selector, cls);

  return cls;
};
